<template>
  <div>
    <Teleport v-if="mounted" to="#header-breadcrumbs">
      <SBreadcrumb v-bind="{ routes }" v-if="isDesktop" />
      <CurrentPlan
        v-if="isMobile"
        :loading="reasonLoading"
        @handleCheckUserStopReason="handleCheckUserStopReason"
      />
    </Teleport>
    <div class="text-center mb-6 hidden md:block">
      <h1
        class="text-3xl md:text-[48px] font-interDisplay font-semibold text-dark-300 leading-130"
      >
        {{ $t("plan_title") }}
      </h1>
      <p
        class="text-base md:text-xl text-gray-450 font-medium md:font-semibold"
      >
        {{ $t("plan_subtitle") }}
      </p>
    </div>
    <Transition name="fade" mode="out-in">
      <div :key="loading">
        <template v-if="loading">
          <div class="sm:col-span-3 min-h-[200px] flex-y-center justify-center">
            <div class="spinner"></div>
          </div>
        </template>
        <template v-else>
          <template v-if="list?.length">
            <CTabFull
              active-items-class="!text-dark-300"
              v-model="currentTab"
              :list="tabList"
            />
            <div
              class="w-full flex-y-center justify-center flex-col mb-6 gap-5"
            >
              <button
                @click="showPromocode"
                class="text-dark underline hover:text-green transition-200 text-sm md:text-xl"
              >
                {{ $t("do_you_have_promocode") }}
              </button>
              <label class="w-full flex-y-center justify-center gap-2">
                <span class="text-sm md:text-xl">
                  {{ $t("auto_renew_plan") }}:
                </span>
                <FToggle v-model="planReNew" />
              </label>
            </div>
            <Transition name="fade" mode="out-in">
              <div
                :key="currentTab"
                class="w-full items-stretch grid md:grid-cols-2 xl:grid-cols-4 gap-2 md:gap-4"
              >
                <CPlanCard
                  v-for="(card, index) in list"
                  v-bind="{ card, index }"
                  :is-current-tab="currentTab"
                  :key="index"
                  @next="showModal"
                  @click="selectTariff(card?.id)"
                  @showPlanInfo="handleShowPlanInfo"
                />
              </div>
            </Transition>
          </template>
          <template v-else>
            <CNodata
              image="/images/svg/no-data/no-transaction.svg"
              :title="$t('no_results')"
              :subtitle="''"
            />
          </template>
        </template>
      </div>
    </Transition>
    <CDialog
      :show="modal"
      @close="modal = false"
      :title="
        !isCheckBalance ? $t('insufficient_funds') : $t('confirm_connection')
      "
    >
      <div class="p-5">
        <p
          v-if="!isCheckBalance"
          class="text-center text-sm font-normal leading-normal mb-5"
        >
          {{ $t("insufficient_funds_text") }}
        </p>
        <div
          :class="{ 'p-3 rounded-lg border border-dark/5': true }"
          class="w-full flex flex-col items-center"
        >
          <div
            class="flex-center gap-1 min-w-[224px] py-3 rounded-xl shadow-[0px_0px_30px_0px_rgba(56,_71,_109,_0.09)]"
          >
            <div
              class="w-6 h-6 flex-y-center justify-center object-cover"
              v-html="selectedCard?.svg_icon"
            />
            <p
              class="text-dark-300 text-sm leading-130 font-semibold mt-0.5 gap-1 uppercase"
            >
              {{ selectedCard?.title }}
            </p>
          </div>
          <p
            v-if="!isCheckBalance"
            class="text-blue-100 flex-y-center gap-2 mt-2"
          >
            <span class="leading-130 text-3.5xl font-bold text-green">
              {{
                currentTab === "monthly"
                  ? selectedCard?.price_month > 0
                    ? `${formatMoneyDecimal(selectedCard?.price_month)}$`
                    : $t("free")
                  : selectedCard?.price_year > 0
                    ? `${formatMoneyDecimal(selectedCard?.price_year)}$`
                    : $t("free")
                      ? `${formatMoneyDecimal(
                          currentTab === "monthly"
                            ? selectedCard?.price_month
                            : selectedCard?.price_year,
                          0,
                        )}$`
                      : $t("free")
              }}
            </span>
            <span
              v-if="selectedCard?.price_year > 0"
              class="text-dark/50 leading-130 text-2xl"
              >{{ currentTab === "monthly" ? $t("month") : $t("year") }}</span
            >
          </p>
        </div>
        <i18n-t
          v-if="isCheckBalance"
          keypath="confirm_connection_text"
          tag="p"
          class="text-sm text-center leading-normal text-dark mt-5"
        >
          <template #tariff>
            <span class="font-semibold">
              {{ selectedCard?.title }}
            </span>
          </template>
        </i18n-t>
        <div class="flex gap-5 w-full mt-5">
          <CButton
            :text="$t('cancel')"
            class="w-full"
            variant="info"
            @click="modal = false"
          />
          <RouterLink
            class="w-full block"
            :to="!isCheckBalance ? '/wallet' : ''"
          >
            <CButton
              :text="$t(!isCheckBalance ? 'fill_wallet' : 'confirm')"
              class="w-full"
              :loading="isLoading"
              @click="saveSelectTariff"
            />
          </RouterLink>
        </div>
      </div>
    </CDialog>
    <CDialog
      :show="showPromo"
      @close="closePromocode"
      :title="$t('promocode_title')"
      custom-class="max-w-[375px]"
    >
      <div class="p-5">
        <FGroup :label="$t('enter_promocode')">
          <FInput
            :placeholder="$t('promocode_text')"
            v-model="promocode.values.text"
            :error="promocode.$v.value.$error"
            maxlength="40"
            class="mb-4"
          />
        </FGroup>
        <CButton
          :text="$t('confirm')"
          class="w-full"
          :disabled="Promoloading"
          :loading="Promoloading"
          @click="savePromocode"
        />
      </div>
    </CDialog>
    <ChangeCurrentPlanDialog
      :show="showCurrentPlanChange"
      @close="showCurrentPlanChange = false"
      @confirm="handleChangeCurrentPlan"
    />
    <PlanWarning
      :show="showPlanWarning"
      @close="showPlanWarning = false"
      :type="planType"
    />
    <PlanInfoDialog
      :show="showPlanInfo"
      :plan="currentPlanInfo"
      @close="showPlanInfo = false"
    />
    <BuyStopReason
      :loading="activateLoading"
      :show="showReason"
      :type="reasonType"
      @close="showReason = false"
      @activateBots="handleActivateBots"
    />
  </div>
</template>

<script setup lang="ts">
import FToggle from "@/components/Form/FToggle.vue";
import CTabFull from "@/components/Common/Tab/CTabFull.vue";
import { computed, onMounted, ref, watch } from "vue";
import CPlanCard from "@/modules/Plan/components/CPlanCard.vue";
import CDialog from "@/components/Common/Dialog/CDialog.vue";
import CButton from "@/components/Common/CButton.vue";
import { debounce, formatMoneyDecimal } from "@/utils";
import ApiService from "@/services/ApiService";
import CNodata from "@/components/Common/CNodata.vue";
import { useAuthStore } from "@/modules/Auth/stores";
import { useCustomToast } from "@/composables/useCustomToast";
import { useI18n } from "vue-i18n";
import { trackCustomEvent } from "@/utils/fbq";

import FInput from "@/components/Form/Input/FInput.vue";
import FGroup from "@/components/Form/FGroup.vue";
import { useForm } from "@/composables/useForm";
import { required } from "@vuelidate/validators";
import { useMounted } from "@/composables/useMounted";
import PlanWarning from "@/components/Common/Dialog/PlanWarning.vue";
import { useMobile } from "@/composables/useMobile";
import SBreadcrumb from "@/components/Common/CBreadcrumb.vue";
import CurrentPlan from "@/modules/Plan/components/CurrentPlan.vue";
import ChangeCurrentPlanDialog from "@/modules/Plan/components/ChangeCurrentPlanDialog.vue";
import PlanInfoDialog from "@/modules/Plan/components/PlanInfoDialog.vue";
import BuyStopReason from "@/components/Common/Dialog/BuyStopReason.vue";
import { useLocalizedNavigation } from "@/composables/useLocalizedNavigation";

const { locale } = useI18n();
import { usePushToDataLayer } from "@/composables/usePushToDataLayer";
const { pushToDataLayer } = usePushToDataLayer();
const { mounted } = useMounted();

const store = useAuthStore();

const { showToast } = useCustomToast();
const { t } = useI18n();

const { isMobile } = useMobile("mobile");
const { isDesktop } = useMobile("desktop");

const selectedCard = ref();

const showPlanInfo = ref(false);
const loading = ref(true);
const isLoading = ref(false);
const Promoloading = ref(false);

const showCurrentPlanChange = ref(false);

const showPlanWarning = ref(false);
const planType = ref("");

const selectedTariff = ref(null);
const currentTab = ref("monthly");

const user = computed(() => useAuthStore()?.user);

const planReNew = ref(user.value?.auto_renew_plan || false);
const tabList = computed(() => [
  {
    label: t("monthly"),
    value: "monthly",
  },
  {
    label: t("annually"),
    value: "yearly",
  },
]);

const currentPlanInfo = ref([]);
const handleShowPlanInfo = (plan) => {
  currentPlanInfo.value = plan;
  if (currentPlanInfo.value) {
    showPlanInfo.value = true;
  }
};

function getList() {
  loading.value = true;
  ApiService.get("/payment/plans/")
    .then((res: any) => {
      list.value = res?.data?.results;
    })
    .finally(() => (loading.value = false));
}
function handleRenewPlan() {
  debounce("search-merchant-search", () => {
    ApiService.post(`/user/auto-renew-plan/`, {
      auto_renew_plan: planReNew.value,
    }).then(() => {
      trackCustomEvent("UserRenewPlanSuccess", {
        event_category: "plan",
        event_label: "renew_plan_success",
        button_text: "Renew Plan",
      });
      showToast(
        t(planReNew.value ? "on_auto_renew_plan" : "off_auto_renew_plan"),
        "success",
      );
      store.fetchUserData();
      getList();
    });
  });
}

watch(
  () => planReNew.value,
  () => {
    handleRenewPlan();
  },
);
const list = ref();
const modal = ref(false);

const handleChangeCurrentPlan = () => {
  showCurrentPlanChange.value = false;
  modal.value = true;
};

const { navigateTo } = useLocalizedNavigation();

const showModal = async (card: number) => {
  if (user.value?.user_bots_count === 0 || !card?.is_free) {
    selectedCard.value = card;
    if (card?.is_free) {
      modal.value = true;
    } else {
      showCurrentPlanChange.value = true;
    }
  } else {
    if (
      card?.is_free &&
      card?.allowed_strategies?.length > user.value?.user_bots_count
    ) {
      showPlanWarning.value = true;
      planType.value = "free";
    } else {
      showPlanWarning.value = true;
      planType.value = "premium";
    }
  }
};

const checkPlanDate = computed(() => {
  return currentTab.value === "monthly"
    ? selectedCard.value?.price_month
    : selectedCard.value?.price_year;
});

const isCheckBalance = computed(() => {
  return (
    Number(user.value?.balance?.replace(/\s/g, "")) >= checkPlanDate.value ||
    checkPlanDate.value <= 0
  );
});

const selectTariff = (planID: number) => {
  selectedTariff.value = planID;
};

const saveSelectTariff = () => {
  if (selectedTariff.value) {
    onConfirm(selectedTariff.value);
  }
  document.body.style.overflow = "auto";
};

const onConfirm = (plan_id: number) => {
  isLoading.value = true;
  const data = {
    plan: plan_id,
    payment_type: currentTab.value,
  };

  if (isCheckBalance.value) {
    ApiService.post(`/payment/user-plan/create/`, data)
      .then(() => {
        // Handle success, if needed
        showToast(t("successfully_confirm"), "success");
        store.fetchUserData();
        navigateTo({ name: "PDashboard" });
        trackCustomEvent("UserRenewPlanSuccess", {
          event_category: "plan",
          event_label: "renew_plan_success",
          button_text: "Renew Plan",
        });
      })
      .catch((error) => {
        if (error?.response?.status === 400) {
          showToast(error?.response?.data?.[0], "error");
        }
        // Handle error, if needed
        console.error("Error while confirming:", error);
      })
      .finally(() => {
        isLoading.value = false;
        modal.value = false;
      });
  }
};

const showPromo = ref(false);
const promocode = useForm(
  {
    text: "",
  },
  {
    text: {
      required,
    },
  },
);
const authStore = useAuthStore();

const showPromocode = () => {
  showPromo.value = true;
};
const closePromocode = () => {
  showPromo.value = false;
  promocode.values.text = "";
  promocode.$v.value.$reset();
};
const savePromocode = () => {
  promocode.$v.value.$touch();
  if (!promocode.$v.value.$invalid) {
    Promoloading.value = true;
    ApiService.post("/payment/redeem-promo-code/", {
      promo_code: promocode.values.text,
    })
      .then(() => {
        trackCustomEvent("UserPromocodeSuccess", {
          event_category: "plan",
          event_label: "promocode_success",
          button_text: "Promocode",
        });
        showToast(t("promocode_successfully_confirm"), "success");
        authStore.fetchUserData();
        getList();
        closePromocode();
      })
      .catch(({ response }) => {
        if (response.status === 400) {
          showToast(t("promocode_apply_error"), "error");
        } else {
          showToast(response?.data?.promo_code?.[0], "error");
        }
      })
      .finally(() => (Promoloading.value = false));
  }
};

const routes = computed(() => [
  {
    name: t("plan"),
    route: "/plans",
  },
]);

onMounted(() => {
  getList();
  pushToDataLayer("pageview", {
    page: "plan",
    event_category: "page",
    event_label: "plan_page_view",
    button_text: "None",
  });
});

const reasonLoading = ref(false);
const activateLoading = ref(false);
const showReason = ref(false);
const reasonType = ref("plan");

const handleCheckUserStopReason = () => {
  reasonLoading.value = true;
  ApiService.get("user/is-stop-buy-reason/")
    .then((res) => {
      if (res?.data?.is_stop_buy_reason === null) {
        reasonType.value = "success";
      }
      if (res?.data?.is_stop_buy_reason === "balance") {
        reasonType.value = "balance";
      }
      if (res?.data?.is_stop_buy_reason === "plan") {
        reasonType.value = "plan";
      }
      showReason.value = true;
    })
    .finally(() => {
      reasonLoading.value = false;
    });
};
const handleActivateBots = () => {
  activateLoading.value = true;
  ApiService.get("/user/activate-bots/")
    .then(() => {
      trackCustomEvent("UserActivateBotsSuccess", {
        event_category: "plan",
        event_label: "activate_bots_success",
        button_text: "Activate Bots",
      });
      showToast(t("bot_activated"), "success");
      showReason.value = false;
    })
    .finally(() => {
      activateLoading.value = false;
    });
};
watch(
  () => locale.value,
  () => {
    getList();
  },
);
</script>

<style scoped>
.spinner {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background:
    radial-gradient(farthest-side, #16cc53 94%, #0000) top/6.4px 6.4px no-repeat,
    conic-gradient(#0000 30%, #16cc53);
  -webkit-mask: radial-gradient(
    farthest-side,
    #0000 calc(100% - 6.4px),
    #000 0
  );
  animation: spinner-c7wet2 1s infinite linear;
}

@keyframes spinner-c7wet2 {
  100% {
    transform: rotate(1turn);
  }
}
</style>
